import React, { useState, useEffect } from "react";
import { Card, Grid, Icon, TextField } from "@mui/material";
import { Autocomplete } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import { autoCompleteProps } from "components/CommonComponent/CommonFunction";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useDispatch, useSelector } from "react-redux";
import {
  DATE_RANGE_OPTIONS,
  MASTER_OPTIONS,
  ACTION_OPTIONS,
} from "components/common/constant";
import { getDepartmentList } from "actions/department";
import {
  getMasterChangeLogList,
  exportMasterChangeLogReportData,
} from "actions/reports";
import { masterChangeLogReportList } from "constants/reports";
import { useNotify } from "components/CommonComponent/NotificationProvider";

function MasterChangeLogReport() {
  const dispatch = useDispatch();
  const [pageNum, setPageNum] = useState(1);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [showClearButton, setShowClearButton] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const today = dayjs().format("YYYY-MM-DD");

    return {
      dateRange: "TODAY",
      startDate: today,
      endDate: today,
      master: "",
      action: "",
      doneBy: "",
      department: "",
      filterParams: JSON.stringify([
        {
          field: "Date",
          condition: "gte",
          filterValues: [today],
        },
        {
          field: "Date",
          condition: "lte",
          filterValues: [today],
        },
      ]),
    };
  });

  const { notify } = useNotify();

  const departmentListData = useSelector(
    (state) => state.department.departmentList,
  );

  const reportListData = useSelector(
    (state) => state.employeeActivityReport?.masterChangeLogReportList,
  );

  const reportTableData =
    reportListData?.data?.masterChangeLogReport || reportListData?.data || [];
  const tableData =
    reportTableData?.length > 0
      ? reportTableData.map((item, index) => ({
          srNo: index + 1,
          date: item.date ? dayjs(item.date).format("DD MMM YYYY") : "-",
          master: item.master || "-",
          action: item.action || "-",
          value: item.value || "-",
          doneBy: item.doneBy || "-",
          role: item.role || "-",
          department: item.department || "-",
        }))
      : [];

  useEffect(() => {
    if (reportListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords:
          (reportListData?.data?.masterChangeLogReport?.length === 0 &&
            reportListData?.data?.masterChangeLogReport !== undefined) ||
          (Array.isArray(reportListData?.data) &&
            reportListData?.data.length === 0),
        showLoaderOnClick: false,
      });
    } else if (reportListData?.code || reportListData?.message) {
      setListState({
        isLoading: false,
        error:
          reportListData?.message || "Failed to load master change log report!",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [reportListData]);

  useEffect(() => {
    getMasterChangeLogList(dispatch, pageNum, filters.filterParams);

    return () => {
      dispatch({
        type: masterChangeLogReportList,
        payload: {},
      });
    };
  }, [dispatch, pageNum, filters.filterParams]);

  useEffect(() => {
    getDepartmentList(dispatch);
  }, [dispatch]);

  const getDateRange = (days) => {
    const today = new Date();

    if (days === "TODAY") {
      const todayString = dayjs(today).format("YYYY-MM-DD");

      return {
        startDate: todayString,
        endDate: todayString,
      };
    }

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() - 1);

    if (days === "1") {
      const yesterdayString = dayjs(endDate).format("YYYY-MM-DD");

      return {
        startDate: yesterdayString,
        endDate: yesterdayString,
      };
    }

    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (Number(days) - 1));

    return {
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      endDate: dayjs(endDate).format("YYYY-MM-DD"),
    };
  };

  const columns = [
    { Header: "Sr No", accessor: "srNo", width: "110px" },
    { Header: "Date", accessor: "date", width: "120px" },
    { Header: "Master", accessor: "master", width: "130px" },
    { Header: "Action", accessor: "action", width: "130px" },
    { Header: "Value", accessor: "value", width: "130px" },
    { Header: "Done By", accessor: "doneBy", width: "140px" },
    { Header: "Role", accessor: "role", width: "180px" },
    { Header: "Department", accessor: "department", width: "180px" },
  ];

  const buildFilterParams = () => {
    const filterParams = [];

    filterParams.push({
      field: "Date",
      condition: "gte",
      filterValues: [filters.startDate],
    });

    filterParams.push({
      field: "Date",
      condition: "lte",
      filterValues: [filters.endDate],
    });

    if (filters.master) {
      filterParams.push({
        field: "Master",
        condition: "eq",
        filterValues: [filters.master],
      });
    }

    if (filters.action) {
      filterParams.push({
        field: "Action",
        condition: "eq",
        filterValues: [filters.action],
      });
    }

    if (filters.doneBy) {
      filterParams.push({
        field: "DoneBy",
        condition: "like",
        filterValues: [filters.doneBy],
      });
    }

    if (filters.department) {
      filterParams.push({
        field: "Department",
        condition: "like",
        filterValues: [filters.department],
      });
    }

    return JSON.stringify(filterParams);
  };

  const handleSearch = () => {
    const filterParams = buildFilterParams();

    if (filterParams === filters.filterParams) {
      return;
    }

    setListState((prev) => ({
      ...prev,
      isLoading: true,
      showLoaderOnClick: true,
    }));

    setFilters((prev) => ({
      ...prev,
      filterParams,
    }));

    setShowClearButton(true);

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const handleClear = () => {
    const today = dayjs().format("YYYY-MM-DD");

    setFilters({
      dateRange: "TODAY",
      startDate: today,
      endDate: today,
      master: "",
      action: "",
      doneBy: "",
      department: "",
      filterParams: JSON.stringify([
        {
          field: "Date",
          condition: "gte",
          filterValues: [today],
        },
        {
          field: "Date",
          condition: "lte",
          filterValues: [today],
        },
      ]),
    });

    setShowClearButton(false);

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const downloadMasterChangeLogReportCSV = async () => {
    try {
      setExportLoading(true);

      const res = await exportMasterChangeLogReportData(filters.filterParams);
      
      const url = window.URL.createObjectURL(res.data);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "MasterChangeLogReport.csv");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      notify(
        res?.message || "MasterChangeLogReport file downloaded successfully",
        "success",
      );
    } catch (error) {
      notify("Failed to download masterChangeLogReport file", "error");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox width="100%">
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ width: "100%" }}>
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Master Change Log Report
                </MDTypography>

                <MDBox display="flex" gap={2}>
                  <MDButton
                    variant="gradient"
                    color="light"
                    startIcon={<Icon>file_upload</Icon>}
                    sx={{
                      py: 1.2,
                      px: 3,
                      fontWeight: "bold",
                      textTransform: "none",
                    }}
                    onClick={downloadMasterChangeLogReportCSV}
                  >
                    {exportLoading ? (
                      <>
                        <i className="fa fa-spinner fa-spin mr-2" />
                        Exporting...
                      </>
                    ) : (
                      "Export Report"
                    )}
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox px={3} pb={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={DATE_RANGE_OPTIONS}
                      value={
                        DATE_RANGE_OPTIONS.find(
                          (item) => item.value === filters.dateRange,
                        ) || null
                      }
                      getOptionLabel={(option) => option.label}
                      onChange={(event, value) => {
                        if (!value) return;

                        if (value.value === "CUSTOM") {
                          setFilters({
                            ...filters,
                            dateRange: "CUSTOM",
                          });
                        } else {
                          const dates = getDateRange(value.value);

                          setFilters({
                            ...filters,
                            dateRange: value.value,
                            startDate: dates.startDate,
                            endDate: dates.endDate,
                          });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Date Range"
                        />
                      )}
                    />
                  </Grid>

                  {filters.dateRange === "CUSTOM" && (
                    <>
                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            label="Start Date"
                            value={dayjs(filters.startDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                startDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            minDate={dayjs(filters.startDate)}
                            label="End Date"
                            value={dayjs(filters.endDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                endDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={MASTER_OPTIONS}
                      value={
                        MASTER_OPTIONS.find(
                          (item) => item.value === filters.master,
                        ) || null
                      }
                      getOptionLabel={(option) => option.label}
                      onChange={(event, value) =>
                        setFilters({
                          ...filters,
                          master: value?.value || "",
                        })
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Master" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={ACTION_OPTIONS}
                      value={
                        ACTION_OPTIONS.find(
                          (item) => item.value === filters.action,
                        ) || null
                      }
                      getOptionLabel={(option) => option.label}
                      onChange={(event, value) =>
                        setFilters({
                          ...filters,
                          action: value?.value || "",
                        })
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Action" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Done By"
                      value={filters.doneBy}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          doneBy: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={departmentListData?.data?.Department || []}
                      getOptionLabel={(option) => option?.name || ""}
                      value={
                        departmentListData?.data?.Department?.find(
                          (item) => item.name === filters.department,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          department: newValue?.name || "",
                          departmentCode: newValue?.code || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.name === value?.name
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Department"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg="auto">
                    <MDBox display="flex" gap={1}>
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {showClearButton && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox px={3} py={1}>
                <MDTypography variant="body1" color="deepBlue">
                  Total Records:{" "}
                  {reportListData?.data?.totalRecords || tableData.length}
                </MDTypography>
              </MDBox>

              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {listState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : listState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="error">
                      {listState.error}
                    </MDTypography>
                  </MDBox>
                ) : listState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="text">
                      No records found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    columns={columns}
                    data={tableData}
                    isSorted={true}
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (reportListData?.data?.totalRecords || tableData.length) /
                        (reportListData?.data?.noOfRecordsPerPage ||
                          tableData.length ||
                          1),
                    )}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default MasterChangeLogReport;
